import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  RoomProvider,
  useRoomContext,
  useRoomPhase,
  useRoomParticipants,
  useLiveStateSync,
  normalizeJoinCode,
} from '@interactive-room';
import { SalaShell } from '../components/layout/SalaShell';
import { ActivityFrame } from '../components/ui/ActivityFrame';
import { PHASE_STRIP, MODE_VISUALS } from '../theme/designSystem';
import type { GameMode } from '@interactive-room';

function ProjectorContent() {
  const { code } = useParams<{ code: string }>();
  const { joinAsViewer, state: roomState } = useRoomContext();
  const { phase } = useRoomPhase();
  const { connectedCount } = useRoomParticipants();

  useEffect(() => {
    const joinCode = normalizeJoinCode(code || '');
    if (!joinCode) return;
    void joinAsViewer(joinCode);
  }, [code, joinAsViewer]);

  const gameMode = roomState.state?.core.activity.mode as GameMode | undefined;
  const phaseMeta = PHASE_STRIP[phase];
  const visual = gameMode ? MODE_VISUALS[gameMode] : null;
  const hasActivity = Boolean(roomState.state?.payload && roomState.state.runtime);

  useLiveStateSync(Boolean(roomState.roomId));

  return (
    <SalaShell variant="projector" phase={phase} mode={gameMode ?? null} bare>
      <div className="relative flex min-h-dvh flex-col overflow-hidden">
        {/* Chrome mínimo — esquinas */}
        <div className="pointer-events-none absolute inset-0 z-30 flex flex-col justify-between p-[var(--sala-gutter)]">
          <div className="flex items-start justify-between">
            <div className="sala-glass pointer-events-auto px-4 py-2">
              <p className="sala-label">Edusico Sala</p>
              <p className="font-display text-2xl font-bold tracking-[0.2em] text-[var(--sala-accent-neon)]">
                {code}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="sala-glass pointer-events-auto px-4 py-2 text-right">
                <p className="font-display text-3xl font-bold text-[var(--sala-accent-neon)]">
                  {connectedCount}
                </p>
                <p className="sala-label">conectados</p>
              </div>
              <span
                className={`h-3 w-3 rounded-full ${roomState.connected ? 'bg-emerald-400 shadow-[0_0_12px_#34d399]' : 'bg-slate-500'}`}
              />
            </div>
          </div>

          <div className="flex items-end justify-between">
            <div
              className="sala-glass pointer-events-auto flex items-center gap-3 px-4 py-2"
              style={{ borderColor: phaseMeta.color }}
            >
              <span className="h-2 w-2 rounded-full" style={{ background: phaseMeta.color }} />
              <span className="font-display text-sm font-bold uppercase tracking-wider" style={{ color: phaseMeta.color }}>
                {phaseMeta.label}
              </span>
              <span className="text-xs text-[var(--sala-ink-dim)]">{phaseMeta.description}</span>
            </div>

            {visual && (
              <div className="sala-glass pointer-events-auto px-4 py-2">
                <span className="font-display text-xs font-bold uppercase tracking-widest" style={{ color: visual.neon }}>
                  {visual.label}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Escena central — pantalla fija sin scroll */}
        <main className="relative z-10 flex min-h-0 flex-1 flex-col px-[var(--sala-gutter)] pb-14 pt-20">
          {hasActivity ? (
            <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-1 flex-col">
              <ActivityFrame role="viewer" phase={phase} mode={gameMode} readOnly fullscreen />
            </div>
          ) : (
            <div className="text-center">
              <p
                className="sala-mega"
                style={{
                  fontSize: 'clamp(2.5rem, 8vw, 6rem)',
                  color: 'var(--sala-ink-muted)',
                }}
              >
                Esperando
              </p>
              <p className="mt-6 font-display text-2xl font-bold text-[var(--sala-accent-neon)]">
                al profesor
              </p>
              <p className="mt-8 sala-code-glyph" style={{ fontSize: 'clamp(3rem, 12vw, 7rem)' }}>
                {code}
              </p>
              <p className="mt-4 text-sm text-[var(--sala-ink-dim)]">
                Los estudiantes entran en <strong className="text-[var(--sala-accent-neon)]">/join</strong>
              </p>
            </div>
          )}
        </main>

        {/* Barra de progreso de fase */}
        <div
          className="absolute inset-x-0 top-0 z-20 h-1"
          style={{
            background: `linear-gradient(90deg, ${phaseMeta.color}, transparent)`,
            opacity: 0.8,
          }}
        />
      </div>
    </SalaShell>
  );
}

export function ProjectorPage() {
  return (
    <RoomProvider>
      <ProjectorContent />
    </RoomProvider>
  );
}
